import { TranslocoDirective, TranslocoService, provideTranslocoScope, translate } from '@jsverse/transloco';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { TaskDetailService } from '../services/task-detail.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ApproveTaskRequest, TaskHistoryStep } from './task-detail.model';
import { PriorityStatus } from 'design-system';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TaskApprovedResponse } from '../../interfaces/task-approved-response';
import { HeaderService, Q8Breakpoints } from 'core';
import { ItemTask, TaskApprovativo } from '../../interfaces/task-approvativo.model';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import {
  TranslocoCurrencyPipe,
  TranslocoDatePipe,
  TranslocoDecimalPipe,
  TranslocoPercentPipe,
} from '@jsverse/transloco-locale';
import { MatMenuModule } from '@angular/material/menu';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { NgTemplateOutlet } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import {
  TaskApprovalConfirmDialog,
  TaskApprovalConfirmDialogData,
} from '../../components/task-approval-confirm-dialog/task-approval-confirm-dialog';
import {
  TaskApprovalResultBottomSheet,
  TaskApprovalResultBottomSheetData,
} from '../../components/task-approval-result-bottom-sheet/task-approval-result-bottom-sheet';
import { TaskProductEditWarningDialog } from '../../components/task-product-edit-warning-dialog/task-product-edit-warning-dialog';
import {
  TaskProductItemEditDialog,
  TaskProductItemEditDialogData,
  TaskProductItemEditDialogResult,
} from '../../components/task-product-item-edit-dialog/task-product-item-edit-dialog';
import {
  TaskProductChangesConfirmDialog,
  TaskProductChangesConfirmDialogData,
} from '../../components/task-product-changes-confirm-dialog/task-product-changes-confirm-dialog';
import { historyFromActivities, historyFromRoles } from '../../utils/task-history.utils';
import { taskAnomalies, toEditProductRows } from '../../utils/task.utils';
import { TaskAttachmentDialog } from '../../components/task-attachment-dialog/task-attachment-dialog';

type PriorityValue = 'low' | 'medium' | 'high';

interface PriorityOption {
  label: string;   // UI
  value: PriorityValue; // backend
}

const EDITABLE_PRODUCT_FIELDS: (keyof ItemTask)[] = [
  'qtaImballi',
  'prezzo',
  'pProvvigione',
  'pNoProfit',
  'pFuoriPremio',
];

@Component({
  providers: [provideTranslocoScope('tasks')],
  selector: 'task-task-detail',
  imports: [
    MatTabsModule,
    MatCardModule,
    MatMenuModule,
    FormsModule,
    MatButtonModule,
    NgTemplateOutlet,
    PriorityStatus,
    TranslocoDirective,
    TranslocoCurrencyPipe,
    TranslocoDatePipe,
    TranslocoDecimalPipe,
    TranslocoPercentPipe,
  ],
  templateUrl: './task-detail.html',
  styleUrl: './task-detail.scss',
})
export class TaskDetail {
  @ViewChild('activityHistoryScroll')
  private activityHistoryScroll?: ElementRef<HTMLElement>;

  // icone selettore priorità
  task: TaskApprovativo;
  currentUserCanEdit = false;

  anomalies: string[] = [];
  history: TaskHistoryStep[] = [];
  readonly changeFieldKeys: Record<string, string> = {
    Quantita: 'products.quantity',
    Prezzo: 'products.unitPrice',
    PercProvvigione: 'products.agentCommission',
    PercNoProfit: 'products.noProfit',
    PercFuoriPremio: 'products.outOfBonus',
  };

  isDesktop = false;
  descriptionCardExpanded = true;
  activityHistoryExpanded = true;

  taskId: string;
  comment = '';
  files: File[] = [];
  productEditMode = false;
  productEditSaving = false;
  originalProductItems: ItemTask[] = [];
  modifiedProductItems: ItemTask[] = [];
  private historyScrollFrame?: number;

  constructor(
      private breakpointObserver: BreakpointObserver,
      private taskDetailService: TaskDetailService,
      private readonly route: ActivatedRoute,
      private snackBar: MatSnackBar,
      private transloco: TranslocoService,
      private headerService: HeaderService,
      private readonly dialog: MatDialog,
      private readonly bottomSheet: MatBottomSheet,
      private readonly router: Router,
    ) {
    this.breakpointObserver
      .observe([`(min-width: ${Q8Breakpoints.desktop}px)`])
      .subscribe(result => {
        this.isDesktop = result.matches;
      });
  }

  ngOnInit(): void {
    this.transloco.selectTranslation('tasks').subscribe(() => {
      this.headerService.setTitle(this.transloco.translate('tasks.taskList.header'));
    });
  }

  ngOnDestroy(): void {
    if (this.historyScrollFrame !== undefined) {
      cancelAnimationFrame(this.historyScrollFrame);
    }
    this.headerService.removeTitle();
  }

  ngAfterViewInit() {
    const routeId = this.route.snapshot.paramMap.get('taskId');

    if (!routeId) {
      console.error('ID task mancante in URL');
      return;
    }

    this.taskId = routeId;
    this.loadTask();
  }

  toggleDescriptionCard(): void {
    if (!this.isDesktop) {
      this.descriptionCardExpanded = !this.descriptionCardExpanded;
    }
  }

  onDescriptionCardKeydown(event: KeyboardEvent): void {
    if (this.isDesktop || (event.key !== 'Enter' && event.key !== ' ')) return;

    event.preventDefault();
    this.toggleDescriptionCard();
  }

  toggleActivityHistoryCard(): void {
    if (!this.isDesktop) {
      this.activityHistoryExpanded = !this.activityHistoryExpanded;
    }
  }

  onActivityHistoryCardKeydown(event: KeyboardEvent): void {
    if (this.isDesktop || (event.key !== 'Enter' && event.key !== ' ')) return;

    event.preventDefault();
    this.toggleActivityHistoryCard();
  }

  loadTask(): void {
    this.taskDetailService.loadTaskDetail(this.taskId).subscribe({
      next: (data: TaskApprovativo) => {
        if(data) {
          this.task = data;
          this.selectedPriorityValue = this.normalizePriority(data.priorita);
          this.currentUserCanEdit = data.currentUserCanEdit;
          this.anomalies = taskAnomalies(data.descrizione);
          this.setHistory(historyFromRoles(data));
          this.loadOrderActivities(data);
        }
      },
      error: err => {
        console.error('Errore caricamento task', err);
      }
    });
  }

  // Per i task nati in App lo storico dell'ordine; finche' non arriva, gli step del task
  private loadOrderActivities(task: TaskApprovativo): void {
    if (task.tipoDettaglio !== 'OrdineApp' || !task.dettaglio?.id) return;

    this.taskDetailService.loadOrderActivities(task.dettaglio.id).subscribe({
      next: activities => this.setHistory(historyFromActivities(activities ?? [], task.id)),
      error: err => console.error('Errore caricamento storico ordine', err),
    });
  }

  private setHistory(history: TaskHistoryStep[]): void {
    this.history = history;
    this.scheduleCurrentHistoryStepCentering();
  }

  private scheduleCurrentHistoryStepCentering(): void {
    if (this.historyScrollFrame !== undefined) {
      cancelAnimationFrame(this.historyScrollFrame);
    }

    this.historyScrollFrame = requestAnimationFrame(() => {
      this.historyScrollFrame = requestAnimationFrame(() => {
        this.centerCurrentHistoryStep();
        this.historyScrollFrame = undefined;
      });
    });
  }

  private centerCurrentHistoryStep(): void {
    const scrollContainer = this.activityHistoryScroll?.nativeElement;
    if (!scrollContainer || scrollContainer.scrollHeight <= scrollContainer.clientHeight) return;

    const currentMarker = scrollContainer.querySelector<HTMLElement>(
      '.activity-history-entry-current .activity-history-marker',
    );
    if (!currentMarker) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const markerRect = currentMarker.getBoundingClientRect();
    const markerCenterFromViewportTop = markerRect.top - containerRect.top + (markerRect.height / 2);
    const targetScrollTop = scrollContainer.scrollTop
      + markerCenterFromViewportTop
      - (scrollContainer.clientHeight / 2);
    const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;

    scrollContainer.scrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
  }

  openProductEditWarning(): void {
    this.dialog
      .open<TaskProductEditWarningDialog, void, boolean>(TaskProductEditWarningDialog, {
        width: 'min(280px, calc(100vw - 32px))',
        maxWidth: '280px',
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.continueProductEdit();
        }
      });
  }

  private continueProductEdit(): void {
    const items = this.task?.dettaglio?.items ?? [];
    this.originalProductItems = items.map(item => ({ ...item }));
    this.modifiedProductItems = items.map(item => ({ ...item }));
    this.productEditMode = true;
  }

  get displayedProductItems(): ItemTask[] {
    return this.productEditMode
      ? this.modifiedProductItems
      : (this.task?.dettaglio?.items ?? []);
  }

  openProductEditor(item: ItemTask): void {
    if (!this.productEditMode || this.productEditSaving) return;

    const data: TaskProductItemEditDialogData = { item };

    this.dialog
      .open<TaskProductItemEditDialog, TaskProductItemEditDialogData, TaskProductItemEditDialogResult>(
        TaskProductItemEditDialog,
        {
          panelClass: 'task-product-item-edit-dialog-panel',
          data,
          width: '560px',
          minWidth: '280px',
          maxWidth: '560px',
        },
      )
      .afterClosed()
      .subscribe(result => {
        if (!result) return;

        const itemIndex = this.modifiedProductItems.findIndex(currentItem => currentItem.id === item.id);
        if (itemIndex < 0) return;

        if (result.action === 'delete') {
          this.modifiedProductItems.splice(itemIndex, 1);
        } else {
          this.modifiedProductItems[itemIndex] = result.item;
        }
      });
  }

  cancelProductEdit(): void {
    this.productEditMode = false;
    this.originalProductItems = [];
    this.modifiedProductItems = [];
  }

  get hasProductChanges(): boolean {
    return toEditProductRows(this.originalProductItems, this.modifiedProductItems).length > 0;
  }

  get modifiedProductFieldsCount(): number {
    const modifiedItemsById = new Map(this.modifiedProductItems.map(item => [item.id, item]));

    return this.originalProductItems.reduce((count, originalItem) => {
      const modifiedItem = modifiedItemsById.get(originalItem.id);
      if (!modifiedItem) return count;

      return count + EDITABLE_PRODUCT_FIELDS.reduce(
        (fieldCount, field) => fieldCount + (originalItem[field] === modifiedItem[field] ? 0 : 1),
        0,
      );
    }, 0);
  }

  get removedProductItemsCount(): number {
    const modifiedItemIds = new Set(this.modifiedProductItems.map(item => item.id));
    return this.originalProductItems.filter(item => !modifiedItemIds.has(item.id)).length;
  }

  getOriginalProductItem(item: ItemTask): ItemTask | undefined {
    return this.originalProductItems.find(originalItem => originalItem.id === item.id);
  }

  hasProductFieldChanged(item: ItemTask, field: keyof ItemTask): boolean {
    return this.getOriginalProductItem(item)?.[field] !== item[field];
  }

  confirmProductEdit(): void {
    if (!this.hasProductChanges || this.productEditSaving) return;

    const data: TaskProductChangesConfirmDialogData = { comment: this.comment.trim() };

    this.dialog
      .open<TaskProductChangesConfirmDialog, TaskProductChangesConfirmDialogData, string>(
        TaskProductChangesConfirmDialog,
        {
          data,
          panelClass: 'task-product-changes-confirm-dialog-panel',
          width: '420px',
          minWidth: '420px',
          maxWidth: '420px',
        },
      )
      .afterClosed()
      .subscribe(comment => {
        if (comment) this.saveProductChanges(comment);
      });
  }

  private saveProductChanges(comment: string): void {
    this.productEditSaving = true;

    this.taskDetailService.editTaskProducts({
      idOrdine: this.task.dettaglio.id,
      commento: comment,
      righe: toEditProductRows(this.originalProductItems, this.modifiedProductItems),
    }).subscribe({
      next: () => {
        this.snackBar.open(
          this.transloco.translate('tasks.taskDetail.products.editConfirmation.success', {
            number: this.task?.dettaglio?.numeroCommessa ?? '-',
          }),
          undefined,
          { duration: 5000 },
        );
        this.router.navigate(['/tasks/list']);
      },
      error: err => {
        this.productEditSaving = false;
        console.error('Errore modifica prodotti', err);
      },
    });
  }

  get priorities(): PriorityOption[] {
    const base = 'tasks.taskDetail.approveTask.priority';
    return [
      { value: 'low', label: this.transloco.translate(`${base}.low`) },
      { value: 'medium', label: this.transloco.translate(`${base}.medium`) },
      { value: 'high', label: this.transloco.translate(`${base}.high`) },
    ];
  }

  get selectedPriority(): PriorityOption {
    const current = this.selectedPriorityValue ?? 'low';
    return this.priorities.find(p => p.value === current) ?? this.priorities[0];
  }

  selectedPriorityValue: PriorityValue = 'low';

  private normalizePriority(priority: string | null | undefined): PriorityValue {
    const normalizedPriority = priority?.trim().toLowerCase();

    return normalizedPriority === 'medium' || normalizedPriority === 'high'
      ? normalizedPriority
      : 'low';
  }

  selectPriority(priority: PriorityOption): void {
    this.selectedPriorityValue = priority.value;
  }

  priorityStatusValue(priority: PriorityValue): string {
    return `${priority.charAt(0).toUpperCase()}${priority.slice(1)}`;
  }

  getAllegatoFileName(path: string): string {
    const fileName = path.split('/').pop();

    if (!fileName) {
      throw new Error(translate<string>('tasks.ui.nomeFileMancantePerPath', { value0: path }));
    }

    return fileName;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.addFiles(input.files);
      input.value = '';
    }
  }

  openAttachmentDialog(): void {
    if (!this.currentUserCanEdit) return;

    this.dialog
      .open<TaskAttachmentDialog, void, File[]>(TaskAttachmentDialog, {
        panelClass: 'task-attachment-dialog-panel',
        width: '560px',
        minWidth: '280px',
        maxWidth: '560px',
        height: '450px',
      })
      .afterClosed()
      .subscribe(files => {
        if (files?.length) this.files = [...this.files, ...files];
      });
  }

  removeFile(index: number): void {
    if (!this.currentUserCanEdit) return;

    this.files = this.files.filter((_, fileIndex) => fileIndex !== index);
  }

  private addFiles(fileList: FileList): void {
    const newFiles = Array.from(fileList);
    this.files = [...this.files, ...newFiles];
  }

  onTaskApprovalManagement(isApproved: boolean): void {
    const dialogData: TaskApprovalConfirmDialogData = {
      isApproved,
      proposalNumber: this.task?.dettaglio?.numeroCommessa ?? '-',
      comment: this.comment.trim(),
    };

    this.dialog
      .open<TaskApprovalConfirmDialog, TaskApprovalConfirmDialogData, boolean>(
        TaskApprovalConfirmDialog,
        {
          data: dialogData,
          width: 'min(560px, calc(100vw - 32px))',
          maxWidth: '560px',
        },
      )
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.submitTaskApproval(isApproved);
        }
      });
  }

  private submitTaskApproval(isApproved: boolean): void {
    const req: ApproveTaskRequest = {
      taskId: this.taskId,
      outcome: isApproved ? 'Approved' : 'Rejected',
      notes: this.comment,
      priorita: this.selectedPriority.value,
      allegati: this.files
    };

    this.taskDetailService.approveTask(req).subscribe({
      next: res => {
        this.resultManagement(res, isApproved);
      },
      error: err => console.error('ERROR', err),
    });
  }

  private resultManagement(res: TaskApprovedResponse, isApproved: boolean): void {
    if(res.result) {
      this.cleaningFields();

      if (this.isDesktop) {
        this.openDesktopApprovalResult(isApproved);
      } else {
        this.loadTask();
        this.openApprovalResult(isApproved);
      }
      console.log('OK', res);
    } else {
      this.snackBar.open(`Errore: ${res.log}`, translate<string>('tasks.ui.close'), { duration: 5000 });
      console.log('Not OK', res);
    }
  }

  private openDesktopApprovalResult(isApproved: boolean): void {
    const messageKey = isApproved ? 'approvedResult' : 'rejectedResult';

    this.router.navigate(['/tasks/list']).then(navigated => {
      if (!navigated) return;

      this.snackBar.open(
        this.transloco.translate(`tasks.taskDetail.approvalFlow.${messageKey}`),
        '×',
        {
          duration: 5000,
        },
      );
    });
  }

  private openApprovalResult(isApproved: boolean): void {
    const data: TaskApprovalResultBottomSheetData = { isApproved };

    this.bottomSheet
      .open<TaskApprovalResultBottomSheet, TaskApprovalResultBottomSheetData, boolean>(
        TaskApprovalResultBottomSheet,
        { data },
      )
      .afterDismissed()
      .subscribe(returnToList => {
        if (returnToList) {
          this.router.navigate(['/tasks/list']);
        }
      });
  }

  downloadAttachment(allegato: { id: string; filePath: string }, taskId: string = this.taskId): void {
    this.taskDetailService.downloadAttachmentService(taskId, allegato.id).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = this.getAllegatoFileName(allegato.filePath);
        anchor.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.snackBar.open(
        this.transloco.translate('tasks.taskDetail.downloadAttachmentError'),
        undefined,
        { duration: 5000 },
      ),
    });
  }

  private cleaningFields(): void {
    this.comment = '';
    this.selectedPriorityValue = 'low';
    this.files = [];
  }

  //TODO: fix temporaneo per convertire da string a numero customerCreditLimitExceeded
  // e i margini unitari, che ERP scrive con il segno in coda: «0,0085- €»
  toNumber(value?: string | number | null): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isNaN(value) ? null : value;
    }

    const text = value.replace('€', '').trim();
    const negative = text.endsWith('-');
    const digits = negative ? text.slice(0, -1).trim() : text;
    if (!digits) {
      return null;
    }

    const normalizedValue = digits.includes(',')
      ? digits.replace(/\./g, '').replace(',', '.')
      : digits;
    const parsedValue = Number(normalizedValue);

    if (Number.isNaN(parsedValue)) {
      return null;
    }

    return negative ? -parsedValue : parsedValue;
  }
}
